import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { LxpLayout } from "@/components/lexpractiq/LxpLayout";
import { getMyMentorProfile, saveMyMentorProfile } from "@/lib/api/lxp-mentors.functions";

export const Route = createFileRoute("/_authenticated/lexpractiq/mentors/apply")({
  head: () => ({ meta: [{ title: "Become a mentor | SA LexPractiq" }] }),
  component: ApplyPage,
});

function ApplyPage() {
  const getFn = useServerFn(getMyMentorProfile);
  const saveFn = useServerFn(saveMyMentorProfile);
  const [p, setP] = useState<any>({
    display_name: "", headline: "", bio: "", specialisations: [], years_experience: 0,
    availability: "", hourly_rate_cents: 0, published: false,
  });
  const [specInput, setSpecInput] = useState("");

  useEffect(() => { getFn().then((r) => { if (r.profile) setP(r.profile); }); }, []);

  async function save() {
    try {
      await saveFn({ data: {
        display_name: p.display_name,
        headline: p.headline || undefined,
        bio: p.bio || undefined,
        specialisations: p.specialisations || [],
        years_experience: p.years_experience ? Number(p.years_experience) : undefined,
        availability: p.availability || undefined,
        hourly_rate_cents: p.hourly_rate_cents ? Number(p.hourly_rate_cents) : undefined,
        published: !!p.published,
      } });
      toast.success("Mentor profile saved");
    } catch (e: any) { toast.error(e?.message || "Save failed"); }
  }

  function addSpec() {
    if (!specInput.trim()) return;
    setP({ ...p, specialisations: [...(p.specialisations || []), specInput.trim()] });
    setSpecInput("");
  }

  return (
    <LxpLayout>
      <div className="lxp-container" style={{ padding: "2rem 1.25rem", maxWidth: 720 }}>
        <h1>Become a mentor</h1>
        <p className="lxp-muted">Publish your mentor profile so students can request Q&amp;A sessions and structured feedback.</p>

        <div className="lxp-card" style={{ display: "grid", gap: "0.75rem" }}>
          <label>
            <div className="lxp-muted">Display name</div>
            <input className="lxp-input" value={p.display_name || ""} onChange={(e) => setP({ ...p, display_name: e.target.value })} />
          </label>
          <label>
            <div className="lxp-muted">Headline</div>
            <input className="lxp-input" placeholder="Labour & CCMA specialist · 8 yrs" value={p.headline || ""} onChange={(e) => setP({ ...p, headline: e.target.value })} />
          </label>
          <label>
            <div className="lxp-muted">Bio</div>
            <textarea className="lxp-textarea" rows={5} value={p.bio || ""} onChange={(e) => setP({ ...p, bio: e.target.value })} />
          </label>
          <div>
            <div className="lxp-muted">Specialisations</div>
            <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap", margin: "0.35rem 0" }}>
              {(p.specialisations || []).map((s: string, i: number) => (
                <span key={i} className="lxp-badge" onClick={() => setP({ ...p, specialisations: p.specialisations.filter((_: string, j: number) => j !== i) })} style={{ cursor: "pointer" }}>{s} ×</span>
              ))}
            </div>
            <div style={{ display: "flex", gap: "0.4rem" }}>
              <input className="lxp-input" value={specInput} onChange={(e) => setSpecInput(e.target.value)} placeholder="e.g. Labour Law" />
              <button className="lxp-btn-ghost" onClick={addSpec}>Add</button>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
            <label>
              <div className="lxp-muted">Years experience</div>
              <input className="lxp-input" type="number" min={0} value={p.years_experience || 0} onChange={(e) => setP({ ...p, years_experience: e.target.value })} />
            </label>
            <label>
              <div className="lxp-muted">Rate (R / hr)</div>
              <input className="lxp-input" type="number" min={0} value={(p.hourly_rate_cents || 0) / 100} onChange={(e) => setP({ ...p, hourly_rate_cents: Number(e.target.value) * 100 })} />
            </label>
          </div>
          <label>
            <div className="lxp-muted">Availability</div>
            <input className="lxp-input" placeholder="Tue & Thu evenings" value={p.availability || ""} onChange={(e) => setP({ ...p, availability: e.target.value })} />
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <input type="checkbox" checked={!!p.published} onChange={(e) => setP({ ...p, published: e.target.checked })} />
            Publish my mentor profile
          </label>
          <div>
            <button className="lxp-btn" onClick={save}>Save profile</button>
          </div>
        </div>
      </div>
    </LxpLayout>
  );
}
